import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import {
  Badge,
  Button,
  Card,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import { Pencil, Trash } from 'phosphor-react'

import { Wrapper } from 'components'
import { RouteNamesAuth } from 'routes/enum'
import { TrainingItem } from 'api/types/trainings'
import { trainingService } from 'api/services/trainings'
import { format } from 'date-fns'

// form validation schema
const validationSchema = Yup.object().shape({
  name: Yup.string().required('Obrigatório'),
})

export const BackofficeTrainings = () => {
  const navigate = useNavigate()

  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isApiBeingCalled, setIsApiBeingCalled] = useState<boolean>(false)

  // track selected training for deletion
  const [selected, setSelected] = useState<string>('')

  // trainings data
  const [data, setData] = useState<TrainingItem[] | null>(null)

  //modals visibility
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)

  // fetch training list from API
  const getTrainingList = async () => {
    setIsLoading(true)
    try {
      const response = (await trainingService.getTrainingList({
        trainingStatus: '3',
      })) as TrainingItem[]
      setData(response || [])
      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar as formações.')
    }
  }

  // delete selected training
  const deleteTraining = async () => {
    if (!selected) return
    try {
      setIsApiBeingCalled(true)
      await trainingService.deleteTraining({ id: selected })
      setIsApiBeingCalled(false)
      setShowDeleteModal(false)
      getTrainingList()

      toast.success('Formação apagada com sucesso.')
    } catch (error) {
      toast.error('Não foi possível apagar a formação.')
    }
  }

  // formik for create campaign form
  const formik = useFormik({
    initialValues: {
      name: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      try {
        const body = {
          name: values.name,
          status: false,
        } as TrainingItem

        const response = await trainingService.saveTraining({
          trainingData: body,
        })

        setShowCreateModal(false)
        getTrainingList()
        toast.success('Formação criada com sucesso.')
        navigate(
          RouteNamesAuth.BACKOFFICE_TRAINING_DETAILS.replace(
            ':id',
            response.toString(),
          ),
        )
      } catch (error) {
        toast.error('Não foi possível criar a formação.')
        console.error('Error creating training:', error)
      }
    },
  })

  // initial data fetch
  useEffect(() => {
    getTrainingList()
  }, [])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <h1 className="pb-10 text-xxxl font-medium text-primary-600 flex items-center justify-between">
          <span>Formações</span>
          <Button
            variant="default"
            color="secondary"
            onClick={() => {
              setShowCreateModal(true)
            }}
          >
            Adicionar Nova Formação
          </Button>
        </h1>

        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Formador</TableHead>
                  <TableHead>Tutorial?</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data &&
                  data.map((training: TrainingItem, index: number) => (
                    <TableRow
                      key={`${training.id}-${index}`}
                      className="even:bg-primary-50/25"
                    >
                      <TableCell className="normal-case">
                        {training.name}
                      </TableCell>

                      <TableCell className="normal-case">
                        {format(
                          new Date(training.date ?? new Date()),
                          'dd/MM/yyyy HH:mm',
                        )}
                      </TableCell>

                      <TableCell className="normal-case">
                        {training.teacher}
                      </TableCell>

                      <TableCell className="normal-case">
                        {training.tutorial ? 'Sim' : 'Não'}
                      </TableCell>

                      <TableCell className="normal-case">
                        <span className="flex items-center gap-2">
                          {training.status ? (
                            <Badge
                              className="pointer-events-none"
                              color="success"
                              variant="base"
                            >
                              Ativo
                            </Badge>
                          ) : (
                            <Badge
                              className="pointer-events-none"
                              color="error"
                              variant="base"
                            >
                              Inativo
                            </Badge>
                          )}
                        </span>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="default"
                            color="secondary"
                            onClick={() => {
                              navigate(
                                RouteNamesAuth.BACKOFFICE_TRAINING_DETAILS.replace(
                                  ':id',
                                  training.id!.toString(),
                                ),
                              )
                            }}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="default"
                            color="error"
                            disabled={isApiBeingCalled}
                            onClick={() => {
                              setSelected(training.id!.toString())
                              setShowDeleteModal(true)
                            }}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </>
        )}
      </Wrapper>

      <Modal open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Formação</ModalTitle>
            <ModalDescription>
              Quer apagar esta formação?
              <br />
              <br />
              <strong className="text-error-500">
                Todos os dados associados a esta formação serão perdidos.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deleteTraining()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={showCreateModal} onOpenChange={setShowCreateModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Criar Formação</ModalTitle>
            <ModalDescription>
              <div className="space-y-4">
                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">
                    Título
                  </h3>
                  <div>
                    <Input
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                    />
                  </div>
                </div>
              </div>
              <br />
              <br />
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="secondary"
              onClick={() => formik.handleSubmit()}
            >
              Criar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
